import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import { FaSpinner, FaCalendar, FaUser, FaClock, FaArrowLeft, FaFacebookF, FaTwitter, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import './BlogDetail.css';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogDetail();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchBlogDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/blogs/${slug}`);
      
      if (response.data.success) {
        setBlog(response.data.data);
        setRelatedBlogs(response.data.relatedBlogs || []);
      } else {
        setError('Blog not found');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err.response?.data?.message || 'Failed to load blog');
    } finally {
      setLoading(false);
    }
  };

  const handleRelatedBlogClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-white">
        <FaSpinner className="animate-spin text-blue-600 text-5xl" />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center max-w-md">
          <h1 className="text-4xl font-black text-gray-900 mb-4">Blog Not Found</h1>
          <p className="text-gray-500 mb-8">{error || 'This blog post could not be found.'}</p>
          <Link to="/blogs" className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full font-bold hover:bg-black transition-all">
            <FaArrowLeft /> Back to All Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-blog-detail-container bg-white pt-12 pb-12 md:pt-12 md:pb-24">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Navigation Breadcrumb */}
        <nav className="mb-6 md:mb-12">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest hover:text-blue-600 transition-colors">
            <FaArrowLeft className="text-[10px]" /> Back to Stories
          </Link>
        </nav>

        {/* Article Header */}
        <header className="mb-8 md:mb-16">
          <div className="flex items-center gap-4 mb-4 md:mb-6">
            <span className="bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full">
              {blog.category}
            </span>
            <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
              <FaClock className="text-[10px]" /> {blog.readingTime}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4 md:mb-8 uppercase tracking-tight leading-[1.1]">
            {blog.title}
          </h1>

          <div className="flex items-center gap-4 border-y border-gray-100 py-3 md:py-6">
            <img src={blog.authorImage} alt={blog.authorName} className="w-12 h-12 rounded-full object-cover" />
            <div>
              <p className="text-xs font-black text-gray-900 uppercase tracking-widest">{blog.authorName}</p>
              <p className="text-xs font-bold text-gray-400">
                {new Date(blog.publishDate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="rounded-3xl overflow-hidden mb-8 md:mb-16 shadow-lg">
          <img src={blog.featuredImage} alt={blog.title} className="w-full h-auto object-cover max-h-[600px]" />
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-16">
          
          {/* Main Body Content */}
          <main className="lg:col-span-8">
            <article className="prose-container">
              {/* Description/Excerpt */}
              <p className="text-2xl font-serif text-gray-600 italic leading-relaxed mb-6 md:mb-12 border-l-4 border-blue-600 pl-8 py-2">
                {blog.description}
              </p>

              {/* Dynamic HTML Content */}
              <div 
                className="blog-main-content text-gray-800 text-lg leading-[1.8] font-medium"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              {/* Tags Section */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="mt-8 pt-4 md:mt-16 md:pt-8 border-t border-gray-100 flex flex-wrap gap-2">
                  {blog.tags.map((tag, idx) => (
                    <span key={idx} className="bg-gray-50 text-gray-500 text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full cursor-default">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </article>
          </main>

          {/* Social Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-32 space-y-6 md:space-y-12">
              
              {/* Share Card */}
              <div className="bg-gray-50 rounded-3xl p-5 md:p-8">
                <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest mb-4 md:mb-6">Share Story</h3>
                <div className="flex gap-3">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-900 hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                    <FaFacebookF size={14} />
                  </a>
                  <a href={`https://twitter.com/intent/tweet?url=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-900 hover:bg-blue-400 hover:text-white transition-all shadow-sm">
                    <FaTwitter size={14} />
                  </a>
                  <a href={`https://wa.me/?text=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-900 hover:bg-green-500 hover:text-white transition-all shadow-sm">
                    <FaWhatsapp size={14} />
                  </a>
                  <a href={`mailto:?subject=${blog.title}`} className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-900 hover:bg-red-500 hover:text-white transition-all shadow-sm">
                    <FaEnvelope size={14} />
                  </a>
                </div>
              </div>

              {/* Related Articles */}
              {relatedBlogs.length > 0 && (
                <div>
                  <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest mb-4 md:mb-6">More like this</h3>
                  <div className="space-y-6">
                    {relatedBlogs.map((relBlog) => (
                      <div 
                        key={relBlog._id} 
                        className="group flex gap-4 cursor-pointer"
                        onClick={() => handleRelatedBlogClick(relBlog.slug)}
                      >
                        <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden shadow-sm">
                          <img src={relBlog.featuredImage} alt={relBlog.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        </div>
                        <div className="flex flex-col justify-center">
                          <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">{relBlog.category}</span>
                          <h4 className="text-sm font-bold text-gray-900 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                            {relBlog.title}
                          </h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </aside>

        </div>

      </div>
    </div>
  );
};

export default BlogDetail;
